
import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../../Common/Layout/Header";
import {
  RiCheckboxCircleLine,
  RiErrorWarningLine,
  RiBusLine,
  RiUserLine,
  RiMapPinLine,
  RiCalendarLine,
  RiPrinterLine,
  RiDownloadLine,
  RiTimeLine,
  RiCloseCircleLine,
  RiMoneyDollarCircleLine,
  RiFileList3Line,
  RiMailLine,
  RiWhatsappLine,
  RiInformationLine,
  RiAlertLine,
  RiShieldCheckLine,
  RiArrowLeftLine,
  RiTicketLine,
} from "react-icons/ri";
import styles from "./BusBookingConfirmation1.module.css";

const BusBookingConfirmation1 = ({ mode, onNavigate, templateId, params, styleConfig }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const themeVariables = {
    "--primary-color": styleConfig?.primary || "#3b82f6",
    "--secondary-color": styleConfig?.secondary || "#8b5cf6",
  };

  const {
    bus, searchQuery, selectedSeats, passengerCount, passengerDetails,
    selectedBoardingPoint, selectedDroppingPoint, totalAmount, baseFare,
    convenienceFee, bookingStatus = "CONFIRMED", bookingId, paymentId, orderId,
    message, bookingNo, pnr, supplierBookingId, invoiceNo,
  } = params || location.state || {};

  const handleNewBooking = () => {
    if (mode === "builder") {
      onNavigate({ name: "bus-search" });
    } else if (mode === "preview") {
      navigate(`/template-preview/${templateId}/bus-search`);
    } else {
      navigate("/template/bus-search");
    }
  };

  // 👇 1. THE MISSING SAFETY CHECK (Prevents White Screen Crashes)
  if (!bus || !selectedSeats || !passengerDetails) {
    return (
      <div className={styles.wrapper} style={themeVariables}>
        <Header />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}>
          <div style={{ textAlign: 'center', padding: '40px', background: 'white', borderRadius: '20px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}>
            <RiAlertLine style={{ fontSize: '4rem', color: '#f59e0b', marginBottom: '20px', margin: '0 auto' }} />
            <h2 style={{ marginBottom: '10px', color: '#1e293b' }}>Ticket Not Found</h2>
            <p style={{ color: '#64748b', marginBottom: '20px' }}>We couldn't find the details for this booking.</p>
            <button 
              onClick={handleNewBooking}
              style={{ padding: '10px 24px', background: 'var(--primary-color, #3b82f6)', color: 'white', border: 'none', borderRadius: '30px', cursor: 'pointer', fontWeight: '600' }}
            >
              Go Back to Search
            </button>
          </div>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return `₹${Math.round(num).toLocaleString("en-IN")}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
    } catch (error) { return dateString; }
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    } catch (error) { return dateString; }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getStatusConfig = (status) => {
    const configs = {
      CONFIRMED: { icon: RiCheckboxCircleLine, title: "Booking Confirmed!", subtitle: "Your bus tickets have been successfully booked", theme: "success", textColor: "success" },
      PAYMENT_SUCCESS: { icon: RiCheckboxCircleLine, title: "Payment Successful!", subtitle: "Your payment was successful. Booking is confirmed.", theme: "success", textColor: "success" },
      PAYMENT_FAILED: { icon: RiCloseCircleLine, title: "Payment Failed", subtitle: "Payment was not completed. Please try again.", theme: "danger", textColor: "danger" },
      PAYMENT_CANCELLED: { icon: RiCloseCircleLine, title: "Payment Cancelled", subtitle: "Payment was cancelled. Please try again.", theme: "warning", textColor: "warning" },
      PAYMENT_SUCCESS_BOOKING_FAILED: { icon: RiErrorWarningLine, title: "Payment Successful - Booking Issue", subtitle: "Payment successful but booking failed. Our team will contact you.", theme: "warning", textColor: "warning" },
      PAYMENT_SUCCESS_BOOKING_PENDING: { icon: RiAlertLine, title: "Payment Successful - Processing", subtitle: "Payment successful. Your booking is being processed.", theme: "info", textColor: "info" },
    };
    return configs[status] || configs.CONFIRMED;
  };

  const handlePrint = () => {
    const ticketContent = generateTicketHTML();
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(ticketContent);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    }
  };

  const handleDownload = () => {
    const ticketContent = generateTicketHTML();
    const blob = new Blob([ticketContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Bus-Ticket-${bookingNo || pnr || 'Booking'}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const generateTicketHTML = () => {
    return `<html><body><h1>Ticket Ready for Print</h1></body></html>`;
  };

  const handleViewBookings = () => {
    if (mode === "builder") {
      alert("My Bookings is disabled in builder mode.");
    } else {
      navigate("/bus/bookings");
    }
  };

  const handleContactSupport = () => {
    const supportPhone = "+91-XXXXXXXXXX";
    const supportEmail = "support@example.com";
    const messageDetails = `Booking Reference: ${bookingNo || bookingId || "N/A"}\nPNR: ${pnr || "N/A"}`;
    const action = window.confirm(`Contact Support:\n\n📞 Call: ${supportPhone}\n📧 Email: ${supportEmail}\n\nClick OK to call, Cancel to email.`);
    if (action) {
      window.location.href = `tel:${supportPhone}`;
    } else {
      window.location.href = `mailto:${supportEmail}?subject=Booking Support - ${bookingNo || bookingId}&body=${encodeURIComponent(messageDetails)}`;
    }
  };

  const statusConfig = getStatusConfig(bookingStatus);
  const StatusIcon = statusConfig.icon;
  const displayMessage = message || statusConfig.subtitle;

  return (
    <div className={styles.wrapper} style={themeVariables}>
     

      <div className={styles.mainContent}>
        <div className={styles.container}>
          <div className={styles.topActions}>
            <button className={styles.backBtn} onClick={handleNewBooking}>
              <RiArrowLeftLine /> Back to Search
            </button>

            <div className={styles.actionBtnGroup}>
              <button className={`${styles.actionBtn} ${styles.printBtn}`} onClick={handlePrint}><RiPrinterLine /> Print Ticket</button>
              <button className={`${styles.actionBtn} ${styles.downloadBtn}`} onClick={handleDownload}><RiDownloadLine /> Download Ticket</button>
              <button className={`${styles.actionBtn} ${styles.viewBtn}`} onClick={handleViewBookings}><RiFileList3Line /> My Bookings</button>
              <button className={`${styles.actionBtn} ${styles.newBtn}`} onClick={handleNewBooking}><RiBusLine /> New Booking</button>
            </div>
          </div>

          <div id="booking-details" className={styles.bookingWrapper}>
            <div className={`${styles.statusBanner} ${styles[statusConfig.theme]}`}>
              <div className={styles.statusIconWrapper}><StatusIcon className={styles.statusIcon} /></div>
              <div className={styles.statusContent}>
                <h2 className={styles.statusTitle}>{statusConfig.title}</h2>
                <p className={styles.statusMessage}>{displayMessage}</p>
                <div className={styles.referenceList}>
                  {bookingNo && <div className={styles.referenceItem}><span className={styles.referenceLabel}>Booking No</span><span className={styles.referenceValue}>{bookingNo}</span></div>}
                  {pnr && <div className={styles.referenceItem}><span className={styles.referenceLabel}>PNR</span><span className={styles.referenceValue}>{pnr}</span></div>}
                  {invoiceNo && <div className={styles.referenceItem}><span className={styles.referenceLabel}>Invoice No</span><span className={styles.referenceValue}>{invoiceNo}</span></div>}
                </div>
              </div>
            </div>

            <div className={styles.twoColumn}>
              <div className={styles.leftColumn}>
                <div className={styles.card}>
                  <div className={styles.cardHeader}><RiBusLine className={styles.cardIcon} /><h3>Journey Details</h3></div>
                  <div className={styles.cardBody}>
                    <div className={styles.routeInfo}>
                      <div className={styles.routePoint}>
                        <div className={styles.pointDot}></div>
                        <div className={styles.pointDetails}>
                          <span className={styles.pointTime}>{formatTime(bus.departureTime)}</span>
                          <span className={styles.pointLocation}>{searchQuery.fromStation}</span>
                          <span className={styles.pointAddress}>{selectedBoardingPoint?.location}</span>
                        </div>
                      </div>
                      <div className={styles.routeConnector}><span className={styles.duration}>{bus.duration || "Direct Journey"}</span></div>
                      <div className={styles.routePoint}>
                        <div className={`${styles.pointDot} ${styles.destinationDot}`}></div>
                        <div className={styles.pointDetails}>
                          <span className={styles.pointTime}>{formatTime(bus.arrivalTime)}</span>
                          <span className={styles.pointLocation}>{searchQuery.toStation}</span>
                          <span className={styles.pointAddress}>{selectedDroppingPoint?.location}</span>
                        </div>
                      </div>
                    </div>
                    <div className={styles.detailsGrid}>
                      <div className={styles.detailItem}><RiCalendarLine className={styles.detailIcon} /><div><span className={styles.detailLabel}>Travel Date</span><span className={styles.detailValue}>{formatDate(searchQuery.travelDate)}</span></div></div>
                      <div className={styles.detailItem}><RiUserLine className={styles.detailIcon} /><div><span className={styles.detailLabel}>Passengers</span><span className={styles.detailValue}>{passengerCount || 0} Passenger(s)</span></div></div>
                      <div className={styles.detailItem}><RiBusLine className={styles.detailIcon} /><div><span className={styles.detailLabel}>Operator</span><span className={styles.detailValue}>{bus.operatorName}</span></div></div>
                      <div className={styles.detailItem}><RiTicketLine className={styles.detailIcon} /><div><span className={styles.detailLabel}>Bus Type</span><span className={styles.detailValue}>{bus.busType || "AC Sleeper"}</span></div></div>
                    </div>
                  </div>
                </div>

                {(selectedBoardingPoint || selectedDroppingPoint) && (
                  <div className={styles.card}>
                    <div className={styles.cardHeader}><RiMapPinLine className={styles.cardIcon} /><h3>Boarding & Dropping Points</h3></div>
                    <div className={styles.cardBody}>
                      <div className={styles.pointsContainer}>
                        {selectedBoardingPoint && (
                          <div className={styles.pointCard}>
                            <h4 className={styles.pointTitle}>Boarding Point</h4>
                            <p className={styles.pointName}><strong>{selectedBoardingPoint.location}</strong></p>
                            <p className={styles.pointTime}><RiTimeLine /> {formatTime(selectedBoardingPoint.time)}</p>
                          </div>
                        )}
                        {selectedDroppingPoint && (
                          <div className={styles.pointCard}>
                            <h4 className={`${styles.pointTitle} ${styles.droppingTitle}`}>Dropping Point</h4>
                            <p className={styles.pointName}><strong>{selectedDroppingPoint.location}</strong></p>
                            <p className={styles.pointTime}><RiTimeLine /> {formatTime(selectedDroppingPoint.time)}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className={styles.card}>
                  <div className={styles.cardHeader}><RiUserLine className={styles.cardIcon} /><h3>Passenger Details</h3></div>
                  <div className={styles.cardBody}>
                    <table className={styles.passengerTable}>
                      <thead><tr><th>Seat</th><th>Passenger Name</th><th>Age</th><th>ID Proof</th><th>Fare</th></tr></thead>
                      <tbody>
                        {selectedSeats.map((seat, index) => {
                          const passenger = passengerDetails[index] || passengerDetails[0];
                          return (
                            <tr key={seat.id}>
                              <td><span className={styles.seatBadge}>{seat.id}</span></td>
                              <td><strong>{passenger?.title} {passenger?.firstName} {passenger?.lastName}</strong><br /><small>{passenger?.email}</small></td>
                              <td>{passenger?.age || "N/A"} years</td>
                              <td><div>{passenger?.idProof || "N/A"}</div><small className={styles.idNumber}>{passenger?.idProofNumber}</small></td>
                              <td>{formatCurrency(seat.totalPriceWithTaxes)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className={styles.rightColumn}>
                {totalAmount && (
                  <div className={`${styles.card} ${styles.summaryCard}`}>
                    <div className={styles.cardHeader}><RiMoneyDollarCircleLine className={styles.cardIcon} /><h3>Fare Summary</h3></div>
                    <div className={styles.cardBody}>
                      <div className={styles.fareList}>
                        <div className={styles.fareItem}><span>Bus Fare</span><span>{formatCurrency(baseFare)}</span></div>
                        <div className={styles.fareItem}><span>Convenience Fee</span><span>{formatCurrency(convenienceFee)}</span></div>
                        <div className={styles.fareDivider}></div>
                        <div className={`${styles.fareItem} ${styles.totalFare}`}><span>Total Amount</span><span className={styles.totalAmount}>{formatCurrency(totalAmount)}</span></div>
                      </div>
                    </div>
                  </div>
                )}

                <div className={`${styles.card} ${styles.supportCard}`}>
                  <div className={styles.cardBody}>
                    <RiInformationLine className={styles.supportIcon} />
                    <h4>Need Help?</h4>
                    <p>If you have any questions, our support team is here to help.</p>
                    <button className={styles.supportBtn} onClick={handleContactSupport}>Contact Support</button>
                  </div>
                </div>

                <div className={styles.securityBadge}>
                  <RiShieldCheckLine /><span>Your booking is confirmed and secure</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusBookingConfirmation1;